import { useTranslation } from "react-i18next";
import {
  barWidthPercent,
  bonusLabel,
  isAllIncluded,
  packageFeatures,
  perFolioLabel,
  priceParts,
  ribbonLabel,
  savingsPercent,
  sortCatalog,
} from "@/utils/packageFeatures.js";
import "./fonts.js";
import "./packages.scss";

/**
 * La escalera de paquetes del diseño del cliente. Todo sale de GET /packages;
 * lo único que cambia entre la landing y el panel es el pie de la tarjeta, que
 * llega por `renderActions`.
 *
 * @param {object[]} packages catálogo tal como lo devuelve el API
 * @param {(pkg: object) => import("react").ReactNode} renderActions pie de la tarjeta
 * @param {string} [selectedId] paquete resaltado (el elegido en /buy_package)
 */
const PackagePlans = ({ packages, renderActions, selectedId }) => {
  const { t } = useTranslation();
  const catalog = sortCatalog(packages);
  // El ahorro más alto del catálogo se pinta en dorado, como en el diseño.
  const maxSavings = catalog.reduce(
    (max, pkg) => Math.max(max, savingsPercent(pkg, catalog)),
    0,
  );

  return (
    <div className="cp-planes">
      {catalog.map((pkg, index) => {
        const previous = index > 0 ? catalog[index - 1] : null;
        const ribbon = ribbonLabel(pkg, previous, t);
        const savings = savingsPercent(pkg, catalog);
        const bonus = bonusLabel(pkg, t);
        const price = priceParts(pkg.priceCents);
        const top = isAllIncluded(pkg);
        const classes = [
          "cp-card",
          `cp-p${Math.min(index + 1, 5)}`,
          top ? "cp-top" : "",
          pkg.id === selectedId ? "cp-elegido" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <article className={classes} key={pkg.id}>
            <div className="cp-cap">
              {ribbon && <div className="cp-cinta">{ribbon}</div>}
              <span className="cp-folio-id">{pkg.name}</span>
              <h3 className="cp-nombre">{pkg.commercialName ?? pkg.name}</h3>
              <div className="cp-precio">
                <sup>{price.symbol}</sup>
                {price.amount}
              </div>
              <div className="cp-unit">
                <span>{perFolioLabel(pkg, t)}</span>
                <span className="cp-barra">
                  <i style={{ width: `${barWidthPercent(pkg, catalog)}%` }} />
                </span>
              </div>
              {bonus ? (
                <span className="cp-ahorro cp-fuerte">{bonus}</span>
              ) : (
                savings > 0 && (
                  <span
                    className={`cp-ahorro${savings === maxSavings ? " cp-fuerte" : ""}`}
                  >
                    {t("packages.savings", { percent: savings })}
                  </span>
                )
              )}
            </div>

            <div className="cp-perf">
              <b />
            </div>

            <ul className="cp-lista">
              {packageFeatures(pkg, previous, t).map((feature) => (
                <li
                  className={`cp-item ${feature.included ? "cp-si" : "cp-no"}`}
                  key={feature.key}
                >
                  <span className="cp-mk" aria-hidden="true">
                    {feature.included ? "✓" : "—"}
                  </span>
                  <span>
                    {feature.label}
                    {feature.isNew && (
                      <em className="cp-nuevo">{t("packages.newInPlan")}</em>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            <div className="cp-pie">{renderActions?.(pkg)}</div>
          </article>
        );
      })}
    </div>
  );
};

export default PackagePlans;
